import React, { useState, useEffect } from "react";
import { Label, Badge, Input } from "@/components/ui";
import { Wallet, X, Search } from "lucide-react";
import financeService from "@/services/financeService";

/**
 * Budget Filter Component
 *
 * Multi-select filter for budgets with search functionality.
 * Requirements: 5.1-5.9
 */
const BudgetFilter = ({ selectedBudgets = [], onChange, subsidiaryId }) => {
  const [budgets, setBudgets] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadBudgets();
  }, [subsidiaryId]);

  const loadBudgets = async () => {
    try {
      setLoading(true);
      const params = {};
      if (subsidiaryId) {
        params.subsidiary_id = subsidiaryId;
      }
      const response = await financeService.getBudgets(params);
      if (response?.data?.success) {
        setBudgets(response.data.data?.budgets || []);
      }
    } catch (error) {
      console.error("Failed to load budgets:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredBudgets = budgets.filter((budget) =>
    budget.name?.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const handleToggleBudget = (budgetId) => {
    const newSelection = selectedBudgets.includes(budgetId)
      ? selectedBudgets.filter((id) => id !== budgetId)
      : [...selectedBudgets, budgetId];
    onChange(newSelection);
  };

  const handleRemoveBudget = (budgetId) => {
    onChange(selectedBudgets.filter((id) => id !== budgetId));
  };

  const getBudgetName = (budgetId) => {
    const budget = budgets.find((b) => b.id === budgetId);
    return budget ? budget.name : budgetId;
  };

  return (
    <div className="space-y-2">
      <Label className="flex items-center gap-2">
        <Wallet className="h-4 w-4" />
        Budgets
      </Label>

      {/* Selected budgets */}
      {selectedBudgets.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {selectedBudgets.map((budgetId) => (
            <Badge
              key={budgetId}
              variant="secondary"
              className="flex items-center gap-1"
            >
              {getBudgetName(budgetId)}
              <X
                className="h-3 w-3 cursor-pointer"
                onClick={() => handleRemoveBudget(budgetId)}
              />
            </Badge>
          ))}
        </div>
      )}

      {/* Search input */}
      <div className="relative">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            type="text"
            placeholder="Search budgets..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onFocus={() => setShowDropdown(true)}
            className="pl-10"
          />
        </div>

        {/* Dropdown */}
        {showDropdown && (
          <div className="absolute z-10 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-y-auto">
            {loading ? (
              <div className="p-4 text-center text-gray-500">
                Loading budgets...
              </div>
            ) : filteredBudgets.length === 0 ? (
              <div className="p-4 text-center text-gray-500">
                No budgets found
              </div>
            ) : (
              filteredBudgets.map((budget) => (
                <div
                  key={budget.id}
                  className={`p-3 cursor-pointer hover:bg-gray-50 ${
                    selectedBudgets.includes(budget.id) ? "bg-blue-50" : ""
                  }`}
                  onClick={() => handleToggleBudget(budget.id)}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium">{budget.name}</div>
                      <div className="text-sm text-gray-600">
                        ₦{budget.allocated_amount?.toLocaleString()}
                      </div>
                    </div>
                    {selectedBudgets.includes(budget.id) && (
                      <div className="text-blue-600">✓</div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {showDropdown && (
        <div
          className="fixed inset-0 z-0"
          onClick={() => setShowDropdown(false)}
        />
      )}
    </div>
  );
};

export default BudgetFilter;
